/**
 * Turns whatever the axios interceptor rejected with into one line a toast
 * can show. The interceptor rejects with the response BODY when there is
 * one, so the shapes seen here are:
 *
 * - ASP.NET model-state problem details:
 *     { title, errors: { field: ['message', ...] } }
 * - Core's domain error envelope:
 *     { error: 'No Analysis Type found.' }
 * - a plain Error / string when the request never reached the server
 * - a Blob when the request asked for `responseType: 'blob'` (downloads)
 *
 * The result is HTML-escaped: ToastAlert renders `message` with `v-html`
 * (other callers rely on `<br />` in their own literals), and an API error
 * can echo user input such as a model name back verbatim.
 */
export async function apiErrorMessage(
  error: unknown,
  fallback = 'The request failed.'
): Promise<string> {
  return escapeHtml(await resolveMessage(error, fallback));
}

// The message lands in element text (not an attribute), so `&`, `<` and `>`
// are the only characters that can open markup there.
const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
};

function escapeHtml(text: string): string {
  return text.replace(/[&<>]/g, (char) => HTML_ESCAPES[char]);
}

async function resolveMessage(
  error: unknown,
  fallback: string
): Promise<string> {
  if (error instanceof Blob) {
    try {
      error = JSON.parse(await error.text());
    } catch {
      return fallback;
    }
  }

  if (typeof error === 'string') return error;

  const body = error as {
    error?: unknown;
    errors?: Record<string, unknown>;
    title?: unknown;
    message?: unknown;
  } | null;

  if (!body || typeof body !== 'object') return fallback;

  if (typeof body.error === 'string' && body.error) return body.error;

  if (body.errors && typeof body.errors === 'object') {
    const lines = Object.entries(body.errors).flatMap(([field, messages]) =>
      (Array.isArray(messages) ? messages : [messages]).map(
        (message) => `${field}: ${String(message)}`
      )
    );
    if (lines.length) return lines.join(' ');
  }

  if (typeof body.title === 'string' && body.title) return body.title;
  if (typeof body.message === 'string' && body.message) return body.message;

  return fallback;
}
